import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import MarkdownRender from 'LuxKit/MarkdownRender'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import ContactUSP from '../ContactUSP'
import OfferDescription from '../OfferDescription'
import BedbankPopularFacilities from './BedbankPopularFacilities'
import BedbankSummaryPromotions from './BedbankSummaryPromotions'
import Image from 'components/Common/Image/Image'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import Heading from 'LuxKit/Typography/Heading'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'

const Description = styled(MarkdownRender)`
  & > h4 + p {
    margin-top: ${rem(8)};
  }
`

interface Props {
  offer: App.BedbankOffer
  location: string
  hasPromotions: boolean
  checkInDate?: string
  checkOutDate?: string
  duration: number
  rooms: Array<OfferPageRoom>
  flightPrice?: number
  flightsEnabled: boolean
}

function BedbankPropertyDetails(props: Props) {
  const {
    offer,
    location,
    hasPromotions,
    checkInDate,
    checkOutDate,
    duration,
    rooms,
    flightPrice,
    flightsEnabled,
  } = props

  const intl = useIntl()
  const facilitiesOverride = offer.property?.facilitiesOverride

  const { expandableDesc, nonExpandableDesc } = useMemo<{ expandableDesc: string | null, nonExpandableDesc: string | null }>(() => {
    const index = offer.description.indexOf('####')
    const mainDesc = offer.description.substring(0, index)
    const additionalDesc = offer.description.slice(index)

    if (mainDesc.length > 50 && additionalDesc) {
      const expandableDesc = mainDesc + '\n' + additionalDesc
      return { expandableDesc, nonExpandableDesc: null }
    } else {
      return { expandableDesc: null, nonExpandableDesc: offer.description }
    }
  }, [offer.description])

  return (
    <Group direction="vertical" gap={16}>
      <Group direction="vertical" gap={8}>
        <Group
          fullWidth
          direction="horizontal"
          horizontalAlign="space-between"
          verticalAlign="center"
          gap={8}
        >
          <Group direction="vertical" gap={4}>
            {(offer.property.locationTitleOverride || offer.property.address) && (
              <BodyTextBlock
                data-testid="location-box"
                variant="small"
                startIcon={<LineMapMarkerIcon />}
              >
                {location} ·{' '}
                <TextLink href="#getting-there">
                  {intl.formatMessage({
                    id: 'bCfs6X7rkK',
                    defaultMessage: 'View on map',
                    description: 'Link text to view property location on map for bedbank property details',
                  })}
                </TextLink>
              </BodyTextBlock>
            )}

            <Heading variant="heading2" as="h1" data-testid="bedbank-offer-name">{offer.name}</Heading>

            {offer.tagline && <BodyText variant="large">{offer.tagline}</BodyText>}
          </Group>
          {offer.logoImageId && (
            <CSSBreakpoint min="desktop">
              <Image
                id={offer.logoImageId}
                dpr={3}
                height={75}
                enhancedFormats={false}
                alt={`${offer.property.name || 'Vendor'} logo`}
              />
            </CSSBreakpoint>
          )}
        </Group>

        {!hasPromotions && (
          <div>
            <ProductTypeLabel productType="bedbank_hotel" />
          </div>
        )}

        {isOfferRatingDisplayable(offer.property.rating) && <OfferRating
          link
          inlineLabel
          variant="small"
          rating={offer.property.rating}
        />}
      </Group>
      <Group direction="vertical" gap={24}>
        {expandableDesc && (
          <OfferDescription description={expandableDesc} />
        )}
        {nonExpandableDesc && (
          <Description type="compact" fontSize="medium" content={nonExpandableDesc} />
        )}
        {hasPromotions && offer.additionalPromoContent && (
          <MarkdownRender
            type="compact"
            fontSize="medium"
            content={offer.additionalPromoContent}
          />
        )}
        {!hasPromotions && !facilitiesOverride && (
          <BedbankPopularFacilities offer={offer} />
        )}
        {hasPromotions && (isLPCTacticalEnabled() || isAgentHubEnabled) &&
          <BedbankSummaryPromotions
            checkInDate={checkInDate}
            checkOutDate={checkOutDate}
            offer={offer}
            duration={duration}
            rooms={rooms}
            flightPrice={flightPrice}
            flightsEnabled={flightsEnabled}
          />}

        {hasPromotions && isLPCTacticalEnabled() && <div><ContactUSP /></div>}
      </Group>
    </Group>
  )
}

export default BedbankPropertyDetails
